"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Video = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const useAvoidIsPhone_1 = require("../../hooks/useAvoidIsPhone");
const IconButton_1 = require("../IconButton");
const OptionDropdown_1 = require("../Input/OptionDropdown");
const Target_1 = require("../Target");
const getTimeDisplay = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time - hours * 3600) / 60);
    const seconds = Math.floor(time - hours * 3600 - minutes * 60);
    if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};
const getVideoLoadPercentSegments = (video) => {
    const { buffered, duration } = video;
    if (!buffered || buffered.length === 0) {
        return [];
    }
    if (isNaN(duration) || duration === 0) {
        return [];
    }
    const loadedPercentSegments = [];
    for (let i = 0; i < video.buffered.length; i++) {
        const start = video.buffered.start(i);
        const end = video.buffered.end(i);
        loadedPercentSegments.push([start / duration, (end - start) / duration]);
    }
    return loadedPercentSegments;
};
exports.Video = react_1.default.forwardRef(({ autoPlay, crossOrigin, loop, muted: mutedInitial, poster, preload = "metadata", sources, sx = {}, textTracks }, ref) => {
    var _a;
    const { maxHeight, maxWidth, height, width } = sx, sxRest = __rest(sx, ["maxHeight", "maxWidth", "height", "width"]);
    const captionDropdownRef = react_1.default.useRef(null);
    const videoRef = react_1.default.useRef(null);
    const isPhoneInBrowser = (0, useAvoidIsPhone_1.useAvoidIsPhone)();
    const [currentTime, currentTimeSet] = react_1.default.useState(0);
    const [captionsFocused, captionsFocusedSet] = react_1.default.useState(false);
    const [, loadedDataSet] = react_1.default.useState(false);
    const [loadedPercentSegments, loadedPercentSegmentsSet] = react_1.default.useState([]);
    const [muted, mutedSet] = react_1.default.useState(mutedInitial !== undefined ? mutedInitial : false);
    const [played, playedSet] = react_1.default.useState(false);
    const [playing, playingSet] = react_1.default.useState(false);
    const [textTrackSelected, textTrackSelectedSet] = react_1.default.useState(null);
    const duration = ((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.duration) || 0;
    const captionOptions = react_1.default.useMemo(() => {
        const options = [
            { label: "Off", selected: textTrackSelected === null, value: null }
        ];
        textTracks === null || textTracks === void 0 ? void 0 : textTracks.forEach((track) => {
            options.push({
                label: track.label,
                selected: track.src === textTrackSelected,
                value: track.src
            });
        });
        return options;
    }, [textTracks, textTrackSelected]);
    react_1.default.useImperativeHandle(ref, () => videoRef.current, []);
    const onSelectCaption = react_1.default.useCallback((option) => {
        if (videoRef.current) {
            for (const track of [...videoRef.current.textTracks]) {
                track.mode = track.label === option.label ? "showing" : "hidden";
            }
        }
        textTrackSelectedSet(option.value);
    }, []);
    const { ariaPropsForDropdown, focusedOptionIdx, handleClickOption, handleKeyDown, iconButtonRef: captionDropdownButtonRef, showDropdown } = (0, OptionDropdown_1.useOptionDropdown)({
        dropdownOptions: captionOptions,
        isFocused: captionsFocused,
        isSelect: true,
        onSelectOption: onSelectCaption
    });
    const handleCaptionButtonClick = react_1.default.useCallback((e) => {
        e.currentTarget.focus();
    }, []);
    const requestFullScreen = react_1.default.useCallback(() => {
        var _a, _b;
        // @ts-ignore The Webkit variant is still required for iOS
        if ((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.webkitEnterFullScreen) {
            // @ts-ignore
            videoRef.current.webkitEnterFullScreen();
        }
        else {
            (_b = videoRef.current) === null || _b === void 0 ? void 0 : _b.requestFullscreen();
        }
    }, []);
    const handleFullscreenClick = react_1.default.useCallback(() => {
        requestFullScreen();
    }, [requestFullScreen]);
    const handleLoadedData = react_1.default.useCallback(() => {
        loadedDataSet(true);
    }, []);
    const handlePlayClick = react_1.default.useCallback(() => {
        var _a;
        (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.play();
        if (isPhoneInBrowser && !played) {
            setTimeout(() => {
                requestFullScreen();
            }, 100);
        }
    }, [isPhoneInBrowser, played, requestFullScreen]);
    const handleMobileOverlayClick = react_1.default.useCallback(() => {
        var _a, _b;
        if (playing) {
            (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        }
        else {
            (_b = videoRef.current) === null || _b === void 0 ? void 0 : _b.play();
        }
        if (isPhoneInBrowser && !played && !playing) {
            setTimeout(() => {
                requestFullScreen();
            }, 100);
        }
    }, [isPhoneInBrowser, played, playing, requestFullScreen]);
    const handlePauseClick = react_1.default.useCallback(() => {
        var _a;
        (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.pause();
    }, []);
    const handleProgress = react_1.default.useCallback(() => {
        if (videoRef.current) {
            loadedPercentSegmentsSet(getVideoLoadPercentSegments(videoRef.current));
        }
    }, []);
    const handleSeekClick = react_1.default.useCallback((e) => {
        if (videoRef.current) {
            const { left, right } = e.currentTarget.getBoundingClientRect();
            const seekPercent = (e.pageX - left) / (right - left);
            currentTimeSet(duration * seekPercent);
            videoRef.current.currentTime = duration * seekPercent;
        }
    }, [duration]);
    const handleTimeUpdate = react_1.default.useCallback((e) => {
        if (!played) {
            playedSet(true);
        }
        currentTimeSet(e.currentTarget.currentTime);
        loadedPercentSegmentsSet(getVideoLoadPercentSegments(e.currentTarget));
    }, [played]);
    const handleVolumeButtonClick = react_1.default.useCallback(() => {
        if (videoRef.current) {
            videoRef.current.muted = !muted;
            mutedSet(!muted);
        }
    }, [muted]);
    return (react_1.default.createElement(foundations_1.Box, { sx: Object.assign(Object.assign({ alignItems: "center", height,
            maxHeight,
            maxWidth, position: "relative", width }, sxRest), { display: "flex", flexDirection: "column", 
            // @ts-ignore
            ":hover > *": {
                opacity: 1,
                transition: "opacity 200ms"
            } }) },
        react_1.default.createElement(foundations_1.Box, { as: "video", autoPlay: autoPlay, controls: false, crossOrigin: crossOrigin, loop: loop, muted: muted, onLoadedData: handleLoadedData, onPause: () => playingSet(false), onPlay: () => playingSet(true), onProgress: handleProgress, onTimeUpdate: handleTimeUpdate, playsInline: true, poster: poster, preload: preload, ref: videoRef, sx: {
                height,
                maxHeight,
                maxWidth,
                width: width !== undefined ? width : "100%"
            } }, sources === null || sources === void 0 ? void 0 :
            sources.map((source, i) => (react_1.default.createElement("source", { key: i, type: source.type, src: source.src }))), textTracks === null || textTracks === void 0 ? void 0 :
            textTracks.map((track, i) => (react_1.default.createElement("track", { key: i, kind: track.kind, label: track.label, src: track.src, srcLang: track.srcLang })))),
        react_1.default.createElement(Target_1.Target, { "aria-label": playing ? "Pause media" : "Play media", "data-testid": "video-mobile-overlay", onClick: handleMobileOverlayClick, sx: {
                display: ["block", "none", null],
                height: "100%",
                left: 0,
                position: "absolute",
                top: 0,
                width: "100%"
            } }),
        react_1.default.createElement(foundations_1.Box, { "data-testid": "video-controls", sx: {
                bottom: 0,
                height: 0,
                opacity: !played ? 1 : 0,
                transition: ["opacity 200ms", null, "opacity 200ms 1000ms"],
                position: "sticky",
                width: "100%"
            } },
            react_1.default.createElement(foundations_1.Box, { sx: {
                    alignItems: "center",
                    bottom: 0,
                    display: "flex",
                    gap: 2,
                    pb: 3,
                    px: 2,
                    position: "absolute",
                    width: "100%"
                } },
                react_1.default.createElement(IconButton_1.IconButton, { "aria-label": playing ? "Pause media" : "Play media", "data-testid": playing ? "video-pause-button" : "video-play-button", onClick: playing ? handlePauseClick : handlePlayClick, icon: playing ? "pause" : "play", shape: "square", sx: { flexShrink: 0 } }),
                react_1.default.createElement(foundations_1.Box, { role: "presentation", sx: {
                        alignItems: "center",
                        backgroundColor: "iconButtonBackgroundColor",
                        borderRadius: "iconButtonBorderRadiusSquare",
                        display: "flex",
                        flexGrow: 1,
                        height: 10,
                        px: 4
                    } },
                    react_1.default.createElement(foundations_1.Box, { "data-testid": "video-progress-bar", onClick: handleSeekClick, sx: {
                            alignItems: "center",
                            cursor: "pointer",
                            display: "flex",
                            height: "100%",
                            width: "100%"
                        } },
                        react_1.default.createElement(foundations_1.Box, { "data-testid": "video-loaded-segments", sx: {
                                backgroundColor: "iconButtonHoverBackgroundColor",
                                borderRadius: "small",
                                height: 1,
                                overflow: "hidden",
                                position: "relative",
                                width: "100%"
                            } },
                            loadedPercentSegments.map((loadedPercentSegment, i) => (react_1.default.createElement(foundations_1.Box, { key: i, sx: {
                                    backgroundColor: "accent01",
                                    height: "100%",
                                    position: "absolute",
                                    opacity: 0.2,
                                    // @ts-ignore
                                    left: `${loadedPercentSegment[0] * 100}%`,
                                    top: 0,
                                    // @ts-ignore
                                    width: `${loadedPercentSegment[1] * 100}%`
                                } }))),
                            react_1.default.createElement(foundations_1.Box, { "data-testid": "video-progress", sx: {
                                    backgroundColor: "accent01",
                                    borderRadius: "small",
                                    height: "100%",
                                    position: "relative",
                                    // @ts-ignore
                                    width: `${(currentTime / duration) * 100}%`
                                } }))),
                    !!duration && (react_1.default.createElement(foundations_1.Box, { "data-duration": duration, "data-testid": "video-time-display", sx: {
                            display: ["none", "block", null],
                            flexShrink: 0,
                            fontSize: 1,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1,
                            paddingInlineStart: 3
                        } },
                        getTimeDisplay(currentTime),
                        " / ",
                        getTimeDisplay(duration)))),
                captionOptions.length > 1 && (react_1.default.createElement(foundations_1.Box, { ref: captionDropdownRef, sx: { position: "relative" }, onBlur: handleClickOption, onKeyDown: handleKeyDown },
                    react_1.default.createElement(OptionDropdown_1.OptionDropdown, { dropdownOptions: captionOptions, focusedIdx: focusedOptionIdx, optionDisplayCount: null, sx: {
                            bottom: () => "calc(100% + 8px)",
                            display: showDropdown ? "block" : "none",
                            right: 0,
                            width: "max-content"
                        } }),
                    react_1.default.createElement(IconButton_1.IconButton, Object.assign({}, ariaPropsForDropdown, { "aria-label": "Change caption settings", "data-testid": "video-caption-button", icon: "closed-captioning", onBlur: () => captionsFocusedSet(false), onClick: handleCaptionButtonClick, onFocus: () => captionsFocusedSet(true), ref: captionDropdownButtonRef, shape: "square" })))),
                react_1.default.createElement(IconButton_1.IconButton, { "aria-label": muted ? "Unmute volume" : "mute volume", "data-testid": muted ? "video-unmute" : "video-mute", onClick: handleVolumeButtonClick, icon: muted ? "speaker-simple-slash" : "speaker-simple-high", shape: "square" }),
                react_1.default.createElement(foundations_1.Box, { sx: { display: [played ? "block" : "none", "block", null] } },
                    react_1.default.createElement(IconButton_1.IconButton, { "aria-label": "Display in fullscreen", "data-testid": "video-fullscreen", onClick: handleFullscreenClick, icon: "arrows-out-simple", shape: "square" }))))));
});
exports.Video.displayName = "Video";
//# sourceMappingURL=Video.js.map